import {
  federationClubFromUser,
  isBelgiumClub,
  isFranceFederationClub,
  isLuxembourgFederationClub,
} from "./clubCountryLanguage";
import {
  formatPointsValue,
  formatRankingPlace,
} from "./flttRankingDisplay";

/**
 * National ranking systems by club/federation.
 * Values stay club-scoped — no cross-country point conversion.
 */
export const RANKING_SYSTEM = {
  FLTT: "FLTT",
  FFTT: "FFTT",
  VTTL: "VTTL",
  AFTT: "AFTT",
  NTTB: "NTTB",
  MANUAL: "MANUAL",
};

function normalizeCountry(value) {
  return String(value || "")
    .trim()
    .toUpperCase()
    .slice(0, 2);
}

function belgiumSystem(club) {
  const blob = [
    club?.club_description,
    club?.club_code,
    club?.club_id,
    club?.club_name,
    club?.federation,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  const code = String(club?.club_code || club?.club_id || "")
    .trim()
    .replace(/^club_be_/i, "")
    .replace(/^club_/i, "")
    .toLowerCase();
  if (/\baftt\b/.test(blob) || /^(bbw|lx|h|l|n)\d/.test(code)) {
    return RANKING_SYSTEM.AFTT;
  }
  return RANKING_SYSTEM.VTTL;
}

export function resolveRankingSystem(club) {
  if (!club) return RANKING_SYSTEM.MANUAL;
  if (isBelgiumClub(club)) return belgiumSystem(club);
  if (isFranceFederationClub(club)) return RANKING_SYSTEM.FFTT;
  if (isLuxembourgFederationClub(club)) return RANKING_SYSTEM.FLTT;
  if (/^club_nl_/i.test(String(club.club_id || ""))) return RANKING_SYSTEM.NTTB;
  const country = normalizeCountry(club.country || club.club_country);
  if (country === "NL") return RANKING_SYSTEM.NTTB;
  if (country === "FR" || country === "MC") return RANKING_SYSTEM.FFTT;
  if (country === "LU") return RANKING_SYSTEM.FLTT;
  if (country === "BE") return belgiumSystem(club);
  return RANKING_SYSTEM.MANUAL;
}

export function rankingSystemLabel(system, t) {
  const key = String(system || RANKING_SYSTEM.MANUAL);
  if (typeof t === "function") {
    const translated = t(`rankingSystem_${key}`, key === "MANUAL" ? "National" : key);
    if (translated) return translated;
  }
  return key === "MANUAL" ? "National" : key;
}

/**
 * Pick ranking fields for the active club from a user / switch payload.
 */
function rankingPayloadMatchesClub(nr, user, club) {
  if (!nr) return false;
  const payloadClub = nr.club_id != null ? String(nr.club_id) : "";
  if (!payloadClub) return true;
  const activeClub = String(user?.club_id || club?.club_id || "");
  return !activeClub || payloadClub === activeClub;
}

function firstPresent(...values) {
  for (const value of values) {
    if (value == null) continue;
    if (typeof value === "string" && value.trim() === "") continue;
    return value;
  }
  return null;
}

export function pickNationalRanking(user, brand) {
  if (!user) return null;
  const nr = user.national_ranking || null;
  const club = federationClubFromUser(user, brand);
  const usePayload = rankingPayloadMatchesClub(nr, user, club);
  const system = usePayload
    ? nr?.ranking_system || resolveRankingSystem(club)
    : resolveRankingSystem(club);
  const flatFieldsBelongToClub =
    !user.ranking_system || String(user.ranking_system) === String(system);

  const classement = usePayload
    ? firstPresent(
        nr?.classement,
        nr?.fltt_classement,
        flatFieldsBelongToClub ? user.fltt_classement : null,
        flatFieldsBelongToClub ? user.classement : null
      )
    : flatFieldsBelongToClub
      ? firstPresent(user.fltt_classement, user.classement)
      : null;
  const points = usePayload
    ? firstPresent(
        nr?.ranking_points,
        nr?.fltt_ranking_points,
        flatFieldsBelongToClub ? user.fltt_ranking_points : null,
        flatFieldsBelongToClub ? user.ranking_points : null
      )
    : flatFieldsBelongToClub
      ? firstPresent(user.fltt_ranking_points, user.ranking_points)
      : null;
  const place = usePayload
    ? firstPresent(
        nr?.ranking_place,
        nr?.fltt_ranking_place,
        flatFieldsBelongToClub ? user.fltt_ranking_place : null
      )
    : flatFieldsBelongToClub
      ? firstPresent(user.fltt_ranking_place)
      : null;

  return {
    ranking_system: system,
    ranking_system_label: usePayload
      ? nr?.ranking_system_label || rankingSystemLabel(system)
      : flatFieldsBelongToClub
        ? user.ranking_system_label || rankingSystemLabel(system)
        : rankingSystemLabel(system),
    country: usePayload
      ? nr?.country || normalizeCountry(club?.country) || null
      : normalizeCountry(club?.country) || null,
    club_id: usePayload
      ? nr?.club_id || user.club_id || club?.club_id || null
      : user.club_id || club?.club_id || null,
    classement: classement != null && String(classement).trim() !== "" ? String(classement) : null,
    ranking_points: points,
    ranking_place: place,
    points_week_delta: usePayload
      ? firstPresent(
          nr?.points_week_delta,
          nr?.fltt_points_week_delta,
          flatFieldsBelongToClub ? user.fltt_points_week_delta : null
        )
      : flatFieldsBelongToClub
        ? firstPresent(user.fltt_points_week_delta)
        : null,
    place_week_delta: usePayload
      ? firstPresent(
          nr?.place_week_delta,
          nr?.fltt_place_week_delta,
          flatFieldsBelongToClub ? user.fltt_place_week_delta : null
        )
      : flatFieldsBelongToClub
        ? firstPresent(user.fltt_place_week_delta)
        : null,
    classement_from: usePayload
      ? firstPresent(
          nr?.classement_from,
          nr?.fltt_classement_from,
          flatFieldsBelongToClub ? user.fltt_classement_from : null
        )
      : flatFieldsBelongToClub
        ? firstPresent(user.fltt_classement_from)
        : null,
    classement_delta: usePayload
      ? firstPresent(
          nr?.classement_delta,
          nr?.fltt_classement_delta,
          flatFieldsBelongToClub ? user.fltt_classement_delta : null
        )
      : flatFieldsBelongToClub
        ? firstPresent(user.fltt_classement_delta)
        : null,
    notice: usePayload
      ? nr?.ranking_notice ?? nr?.notice ?? null
      : flatFieldsBelongToClub
        ? user.fltt_ranking_notice ?? null
        : null,
    rankingNoticePending: Boolean(
      nr?.rankingNoticePending ||
        (nr?.ranking_notice || user.fltt_ranking_notice)?.status === "pending"
    ),
    home_ranking: nr?.home_ranking || user.home_ranking || null,
  };
}

/**
 * One-line display for the active club's national system.
 * e.g. "FLTT · B2 · 1234 pts · #42"
 */
export function formatNationalRankingLine(user, brand, t) {
  const ranking = pickNationalRanking(user, brand);
  if (!ranking) return { line: "N/A", place: null, system: null, ranking: null };

  const system = rankingSystemLabel(ranking.ranking_system, t);
  const points = formatPointsValue(ranking.ranking_points);
  const place = formatRankingPlace(ranking.ranking_place);
  const hasActiveValues = Boolean(ranking.classement || points || place);
  const parts = [];
  if (hasActiveValues && system) parts.push(system);
  if (ranking.classement) parts.push(ranking.classement);
  if (points) parts.push(`${points} pts`);
  if (place) parts.push(place);

  return {
    line: parts.length ? parts.join(" · ") : "N/A",
    place: hasActiveValues ? place : null,
    system: hasActiveValues ? system : null,
    ranking,
    secondary: null,
    isHomeFallback: false,
  };
}
